import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Alert,
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { getTranslation, getTranslationWithParams } from "common";
import { useTranslations } from "hooks";
import {
  SLA_EXPORT_TEXT_KEYS,
  SLA_STATISTICS_EXPORT_FORMATS,
  SLA_STATISTICS_EXPORT_SOURCES,
  slaStatisticsExportDocument,
  slaStatisticsExportTexts,
  type SlaStatisticsExportFormat,
} from "model/slaStatisticsExportModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import {
  slaStatisticsMessageKey,
  type SlaStatisticsRange,
} from "model/slaStatisticsModel";
import type {
  SlaComplianceLevel,
  SlaComplianceResponse,
  SlaDrilldownResponse,
  SlaTrendResponse,
} from "model/slaModel";
import { ReactNode, useMemo, useState } from "react";
import { downloadSlaStatisticsExport } from "utils/slaStatisticsExport";

/**
 * Comando di **export** della superficie Statistiche SLA (piano §5 F4c, §8 riga
 * 128, §6 riga 90: "export raggiungibile" anche su mobile).
 *
 * Il documento e' un **artefatto del client** costruito dai payload correnti: vedi
 * il commento di testa di `model/slaStatisticsExportModel.ts` per la ragione
 * (nessuna rotta di export nel contratto P2) e per l'invariante "nessun cambio
 * della base a insaputa dell'utente" (nessuna seconda richiesta al download).
 *
 * **Gestione degli errori, non silenziosa.** Due esiti dichiarati, entrambi resi
 * con una chiave del catalogo e con un attributo osservabile:
 * - `data-export-error="no_data"` — nessun payload da esportare (periodo oltre il
 *   limite del contratto o conformita' non resa): il comando **dichiara** il motivo
 *   (`sla.export.no_data`) e non produce alcun file; non e' disabilitato, perche'
 *   un comando disabilitato non spiegherebbe nulla;
 * - `data-export-error="failed"` — lo scrittore ha sollevato un errore: il
 *   messaggio e' la chiave d'errore gia' riusata dalle superfici SLA
 *   (`sla.monitor.load_error`), mai un silenzio.
 * Un export riuscito scrive `data-last-export` (formato e nome file), cosi' l'esito
 * e' osservabile senza leggere il filesystem.
 *
 * Il **riepilogo** reso qui (`sla-statistics-export-summary`) espone, riga per riga,
 * le stesse coppie chiave/valore che finiscono nel documento: filtri, periodo,
 * timezone, basi, livello e campione dell'export sono **quelli resi**, e il test
 * puo' confrontare vista e file senza dedurli.
 */
interface SlaStatisticsExportMenuProps {
  locale: string;
  period: SlaStatisticsRange;
  level: SlaComplianceLevel;
  /** Riepilogo dei filtri inviati (`slaStatisticsExportFilterSummary`). */
  filters: string;
  compliance: SlaComplianceResponse | null;
  trend: SlaTrendResponse | null;
  drilldown: SlaDrilldownResponse | null;
}

const FORMAT_ICONS: Record<SlaStatisticsExportFormat, ReactNode> = {
  pdf: <PictureAsPdfOutlinedIcon fontSize="small" />,
  excel: <DescriptionOutlinedIcon fontSize="small" />,
};

const TEST_ID = "sla-statistics-export";

/** Fuori schermo ma impaginato (`innerText` conserva le righe): non `display: none`. */
const VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

const SlaStatisticsExportMenu = ({
  locale,
  period,
  level,
  filters,
  compliance,
  trend,
  drilldown,
}: SlaStatisticsExportMenuProps) => {
  const translations = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<{ code: string; text: string } | null>(null);
  const [lastExport, setLastExport] = useState<string>("");

  const translate = useMemo(
    () =>
      (key: string, params?: Record<string, string | number>) =>
        getTranslationWithParams(translations, key, params),
    [translations],
  );

  const texts = useMemo(() => slaStatisticsExportTexts(translate), [translate]);

  const privacyNote = useMemo(
    () => (compliance ? translate(slaStatisticsMessageKey(compliance, "privacy")) : null),
    [compliance, translate],
  );

  /** Riepilogo reso: le stesse righe di metadati che il documento scrive. */
  const summary = useMemo(
    () =>
      slaStatisticsExportDocument({
        texts,
        locale,
        period,
        level,
        filters,
        compliance,
        trend,
        drilldown,
        privacyNote,
        format: "excel",
      }),
    [texts, locale, period, level, filters, compliance, trend, drilldown, privacyNote],
  );

  const formatLabel = (format: SlaStatisticsExportFormat): string =>
    format === "excel" ? texts.formatExcel : texts.formatPdf;

  const runExport = (format: SlaStatisticsExportFormat) => {
    setAnchorEl(null);
    const exportDoc = slaStatisticsExportDocument({
      texts,
      locale,
      period,
      level,
      filters,
      compliance,
      trend,
      drilldown,
      privacyNote,
      format,
    });

    if (!exportDoc.available) {
      // Nessun file, motivo dichiarato: `sla.export.no_data` del catalogo P1.
      setLastExport("");
      setError({
        code: exportDoc.errorKey ?? SLA_EXPORT_TEXT_KEYS.noData,
        text: exportDoc.errorText ?? texts.noData,
      });
      return;
    }

    try {
      downloadSlaStatisticsExport(exportDoc, format);
      setError(null);
      setLastExport(`${format}:${exportDoc.fileName}`);
    } catch {
      setLastExport("");
      setError({
        code: SLA_MONITOR_TEXT_KEYS.loadError,
        text: getTranslation(translations, SLA_MONITOR_TEXT_KEYS.loadError),
      });
    }
  };

  return (
    <Stack
      spacing={1}
      alignItems="flex-end"
      sx={{ position: "relative" }}
      data-testid={`${TEST_ID}-menu`}
      data-export-formats={SLA_STATISTICS_EXPORT_FORMATS.join(",")}
      data-export-available={summary.available ? "true" : "false"}
      data-last-export={lastExport}
      data-export-source={SLA_STATISTICS_EXPORT_SOURCES.model}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          id={TEST_ID}
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon color="primary" />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          data-testid={TEST_ID}
          sx={{ whiteSpace: "nowrap" }}
        >
          {texts.title}
        </Button>
      </Stack>

      <Menu
        id={`${TEST_ID}-formats`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {SLA_STATISTICS_EXPORT_FORMATS.map((format) => (
          <MenuItem
            key={format}
            id={`${TEST_ID}-${format}`}
            onClick={() => runExport(format)}
            data-testid={`${TEST_ID}-${format}`}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {FORMAT_ICONS[format]}
              <span>{formatLabel(format)}</span>
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      {error && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-error`}
          data-export-error={
            error.code === SLA_EXPORT_TEXT_KEYS.noData ? "no_data" : "failed"
          }
          data-error-key={error.code}
        >
          {error.text}
        </Alert>
      )}

      {/* Riepilogo: gli stessi metadati del documento (filtri/locale correnti).
          Resta nel DOM come contratto vista/export (`sla-statistics-export-summary`)
          ma **non** a schermo: periodo, timezone, basi e campione sono gia' resi
          una volta dalla superficie, e ripeterli qui era il blocco duplicato. */}
      <Stack spacing={0.25} data-testid={`${TEST_ID}-summary`} aria-hidden sx={VISUALLY_HIDDEN}>
        <Typography variant="caption" color="text.secondary">
          {texts.title}
        </Typography>
        {summary.metadata.map((row) => (
          <Stack
            key={row.key}
            direction="row"
            spacing={1}
            alignItems="baseline"
            flexWrap="wrap"
            useFlexGap
            data-testid={`${TEST_ID}-summary-row`}
            data-export-key={row.key}
            data-export-label={row.label}
            data-export-value={String(row.value)}
          >
            <Typography variant="caption" color="text.secondary">
              {row.label}
            </Typography>
            <Typography variant="body2">{String(row.value)}</Typography>
          </Stack>
        ))}
        {summary.tables.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`${TEST_ID}-summary-legend`}
            data-export-legend={summary.legend.join(" | ")}
          >
            {summary.legend.join(" · ")}
          </Typography>
        )}
        {/* Le sezioni del documento, dichiarate: compliance per base, trend, dettaglio. */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {summary.tables.map((table) => (
            <Typography
              key={table.titleKey}
              variant="caption"
              color="text.secondary"
              data-testid={`${TEST_ID}-summary-table`}
              data-export-table={table.titleKey}
              data-export-columns={table.columns.join(" | ")}
              data-export-rows={String(table.rows.length)}
            >
              {`${table.title} (${table.rows.length})`}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SlaStatisticsExportMenu;
